namespace SolovisGateway.Admin.Users {

    /**
     * Confirmation dialog for the "Create Auth0 Account" admin action: provisions an Auth0 account
     * for a user who was never migrated (using the same flow Add User already uses) and sends them a
     * password reset email, or reports that the account already exists and sends nothing.
     *
     * Always visible alongside Send Password Reset Email on the Gateway Admin User Info page, not
     * only surfaced after that action fails. Show it via
     * UserCreateAuth0AccountDialog.showDialog({ userId, contactEmail?, primaryEmail?, endpointUrl?, saveCallback? }):
     *   - userId:       the Gateway user to act on (required).
     *   - contactEmail: the address a reset email would be sent to if one is needed; falls back to
     *                   "this user" if absent.
     *   - primaryEmail: the address the Auth0 account is created/looked up against. When provided,
     *                   it's always shown labeled "(Primary Email)", since that's the identity this
     *                   action actually acts on. contactEmail is shown alongside it, labeled
     *                   "(Contact Email)", only when it's a distinct address, since the reset email
     *                   (if one is sent) is cc'd to it.
     *   - endpointUrl:  optional RPC endpoint to invoke; defaults to the Gateway Admin route. This
     *                   action is Gateway Admin only, so there is no Org Admin variant.
     *   - saveCallback: optional, invoked with the RPC result ({ alreadyExisted: boolean }) after a
     *                   successful call, so the caller can show the right outcome message.
     *
     * Requires an explicit second click on this dialog's own "Create Auth0 Account" button before
     * anything happens; the toolbar click alone only opens this dialog. The send runs via
     * invokeAndWait, so the loading overlay shows while it works and the dialog stays open behind it.
     * On success the dialog closes and saveCallback fires; on failure the error is shown in-dialog and
     * the dialog stays open so the admin can retry.
     */
    export class UserCreateAuth0AccountDialog {

        private _userId;
        private _contactEmail;
        private _primaryEmail;
        private _endpointUrl;
        private _saveCallback;
        private _dialogWindow;
        private _messageDiv;

        constructor(options) {
            var self = this;

            this._userId = options.userId ? options.userId : null;
            this._contactEmail = options.contactEmail ? options.contactEmail : null;
            this._primaryEmail = options.primaryEmail ? options.primaryEmail : null;
            this._endpointUrl = options.endpointUrl ? options.endpointUrl : "~/Admin/UserAdmin/CreateAuth0Account";
            this._saveCallback = options.saveCallback ? options.saveCallback : null;

            var desiredWidth = Math.min($(window).width() * .8, 480);
            var desiredHeight = Math.min($(window).height() * .8, 240);

            var bodyDiv = $("<div>")
                .css("min-height", "60px");

            var messageParagraph = $("<div>").appendTo(bodyDiv);
            messageParagraph.append("This will create an Auth0 account for ");

            if (this._primaryEmail) {
                var contactEmailDiffers = this._contactEmail && this._contactEmail.toLowerCase() !== this._primaryEmail.toLowerCase();
                if (contactEmailDiffers) {
                    messageParagraph.append($("<b>").text(this._contactEmail));
                    messageParagraph.append(" (Contact Email) and ");
                }
                messageParagraph.append($("<b>").text(this._primaryEmail));
                messageParagraph.append(" (Primary Email)");
            }
            else {
                messageParagraph.append($("<b>").text(this._contactEmail ? this._contactEmail : "this user"));
            }

            messageParagraph.append(", if they don't already have one, and immediately send them a password reset email. If they already have an Auth0 account, nothing will be created or sent, and you'll be told so. Continue?");

            this._messageDiv = $("<div>")
                .css({ "display": "none", "margin-top": "10px", "color": "#c62828" })
                .appendTo(bodyDiv);

            this._dialogWindow = new Solovis.UI.Dialog({
                container: bodyDiv,

                title: "Create Auth0 Account",

                modal: true,
                center: true,
                actions: ["Close"],
                autoOpen: true,
                destroyOnClose: true,
                width: desiredWidth,
                minWidth: 100,
                minHeight: 100,
                height: desiredHeight,
                buttons: [
                    {
                        label: "Create Auth0 Account",
                        click: function () {
                            self._send();
                        }
                    },
                    {
                        label: "Cancel",
                        click: function () {
                            self._dialogWindow.close();
                        }
                    }
                ]
            });
        }

        public _send() {
            var self = this;

            self._messageDiv.hide();

            Solovis.Rpc.invokeAndWait(this._endpointUrl,
                {
                    userId: this._userId
                },
                function (response, success) {
                    if (success) {
                        self._dialogWindow.close();
                        if (self._saveCallback) {
                            self._saveCallback(response ? response.result : null);
                        }
                    }
                    else {
                        self._showError(self._extractErrorMessage(response));
                    }
                });
        }

        private _extractErrorMessage(response) {
            if (response && response.error && response.error.message) {
                return response.error.message;
            }
            var modelState = response ? response.result : null;
            if (modelState && modelState.errors && modelState.errors.length) {
                return modelState.errors[0].message ? modelState.errors[0].message : "Failed to create the Auth0 account. Please try again.";
            }
            return "Failed to create the Auth0 account. Please try again.";
        }

        private _showError(message) {
            this._messageDiv
                .css("display", "block")
                .empty()
                .append($("<span>").addClass("fa fa-exclamation-circle").css("margin-right", "6px"))
                .append($("<span>").text(message));
        }

        public static showDialog(options) {
            return new SolovisGateway.Admin.Users.UserCreateAuth0AccountDialog(options);
        }
    }
}
